import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngxs/store';
import { map, Observable, tap } from 'rxjs';

import { NotificationsService } from '@app/core/notification-service';
import { GamesState } from '@app/core/state/games-store';
import { type ConfigConfirmDialog, ConfirmDialogService } from '@app/shared/modules';

import { ActiveAutoplayService } from './active-autoplay.service';

const LEAVE_DURING_AUTOPLAY_DIALOGCONFIG: ConfigConfirmDialog = {
  title: 'GAMES.AUTOPLAY.LEAVE_CONFIRM_HEAD',
  message: 'GAMES.AUTOPLAY.LEAVE_CONFIRM_BODY',
  cancelText: 'PROFILE.RENEW_PROFILE.EDIT_MODAL.IMAGE_MODAL.CANCEL',
  confirmText: 'PROFILE.RENEW_PROFILE.EMAIL_BLOCK.CONFIRM_EM',
  cancelClasses: ['blue-color', 'outline'],
  confirmClasses: ['red-color'],
  width: '520px',
  titleClasses: ['title-large'],
  messageClasses: ['message-regular'],
};

@Injectable({
  providedIn: 'root',
})
export class AutoGameDeactivateGuard {
  readonly #notificationsService = inject(NotificationsService);
  readonly #store = inject(Store);
  readonly #activeAutoplayService = inject(ActiveAutoplayService);
  readonly #confirmDialogService = inject(ConfirmDialogService);

  readonly autoBetIsOn$: Observable<boolean> = this.#store.select(GamesState.autoBetIsOn);

  /** Legacy Crash runs inside the shared bridge iframe and cannot register itself with ActiveAutoplayService directly */
  readonly #autoBetIsOn = toSignal(this.autoBetIsOn$, { initialValue: false });
  readonly #curLegacyGame = toSignal(this.#store.select(GamesState.curLegacyGame), { initialValue: null });
  readonly #isLegacyCrashAutoplayActive = computed(() => this.#autoBetIsOn() && this.#curLegacyGame() === 'crash');

  canDeactivate(): Observable<boolean> | boolean {
    if (this.#activeAutoplayService.isActive() || this.#isLegacyCrashAutoplayActive()) {
      return new Observable<boolean>((subscriber) => {
        this.#confirmDialogService.open(LEAVE_DURING_AUTOPLAY_DIALOGCONFIG, (confirmed) => {
          if (confirmed) {
            this.#activeAutoplayService.stop();
            this.#activeAutoplayService.unregister();
          }
          subscriber.next(confirmed);
          subscriber.complete();
        });

        return () => this.#confirmDialogService.dialogRef?.close();
      });
    }
    return this.autoBetIsOn$.pipe(
      tap((value) => {
        if (value) {
          this.#notificationsService.addInfoNotification('You need to stop auto betting first.');
        }
      }),
      map((value) => !value),
    );
  }
}
